import { EmbedError } from './types.js';
const DEFAULT_MODEL = 'text-embedding-3-small';
const DEFAULT_BASE = 'https://api.openai.com/v1';
export class OpenAIEmbedder {
    model;
    #apiKey;
    #baseUrl;
    #fetch;
    constructor(config = {}) {
        this.model = config.model ?? DEFAULT_MODEL;
        this.#apiKey = config.apiKey ?? process.env.OPENAI_API_KEY ?? '';
        if (!this.#apiKey) {
            throw new EmbedError('OpenAI embedder requires an API key. Set OPENAI_API_KEY or pass `apiKey`.', 'config-error');
        }
        this.#baseUrl = (config.baseUrl ?? process.env.OPENAI_BASE_URL ?? DEFAULT_BASE).replace(/\/$/, '');
        this.#fetch = config.fetch ?? globalThis.fetch;
        if (!this.#fetch) {
            throw new EmbedError('No fetch implementation available (Node 20+ required).', 'config-error');
        }
    }
    async probe() {
        // OpenAI has no cheap model-presence endpoint matching the Ollama pattern;
        // the API doesn't gate by model until you call it. A probe here would
        // either cost a tiny embedding or just a model-list call. We do the list
        // call because it's free and catches obvious auth/network misconfig.
        let res;
        try {
            res = await this.#fetch(`${this.#baseUrl}/models`, {
                headers: { authorization: `Bearer ${this.#apiKey}` },
            });
        }
        catch (e) {
            throw new EmbedError(`OpenAI unreachable at ${this.#baseUrl}: ${e.message}`, 'backend-unreachable');
        }
        if (res.status === 401 || res.status === 403) {
            throw new EmbedError(`OpenAI rejected the API key (${res.status}). Check OPENAI_API_KEY.`, 'config-error');
        }
        if (!res.ok) {
            throw new EmbedError(`OpenAI /models returned ${res.status} ${res.statusText}`, 'http-error');
        }
    }
    async embed(text) {
        let res;
        try {
            res = await this.#fetch(`${this.#baseUrl}/embeddings`, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    authorization: `Bearer ${this.#apiKey}`,
                },
                body: JSON.stringify({ model: this.model, input: text }),
            });
        }
        catch (e) {
            throw new EmbedError(`OpenAI unreachable at ${this.#baseUrl}: ${e.message}`, 'backend-unreachable');
        }
        if (!res.ok) {
            const detail = await res.text().catch(() => '');
            throw new EmbedError(`OpenAI /embeddings returned ${res.status} ${res.statusText}${detail ? `: ${detail}` : ''}`, 'http-error');
        }
        const body = (await res.json());
        const first = body.data?.[0]?.embedding;
        if (!Array.isArray(first) || !first.every((n) => typeof n === 'number')) {
            throw new EmbedError('OpenAI /embeddings response missing a numeric `data[0].embedding` array.', 'malformed-response');
        }
        return first;
    }
}
//# sourceMappingURL=openai.js.map